import * as Sentry from '@sentry/browser';
import { useCookies } from 'react-cookie';

import { IqfuProBuildingStructures, SellingContract } from '@recoil_iqfu/atoms/iqfu_pro/searchQuery';

import { SellingContractType } from '@type-def_iqfu/dashboard/SellingContractType';

import { LandCurrentStatus, ResidenceCurrentStatus } from '@constants_iqfu';
const backendBasePath = process.env.NEXT_PUBLIC_DEV_BACKEND_URL;

const queryString = require('query-string');

export type getSellingContractsMapQueryParams = { town_code: string; property_type: string };

export type SellingContractOfMapPropertyType = 'mansion' | 'land' | 'used_house' | 'new_house';

export type SellingContractOfMap = {
  id: number;
  price: number;
  building_price: null | number;
  land_price: null | number;
  property_type: SellingContractOfMapPropertyType;
  address_summary: string;
  mansion_name: string;
  mansion_full_address: string;
  contract_date: string;
  land_area: number | null;
  building_area: number | null;
  occupied_area: number | null;
  has_reformed: boolean;
  building_structure: keyof typeof IqfuProBuildingStructures | null;
  built_date: string | null;
  floor: number | null;
  floor_plan: string | null;
  current_status: keyof typeof LandCurrentStatus | keyof typeof ResidenceCurrentStatus;
  detail_availability: boolean;
  has_information_map: boolean;
  mansion_id: string | null;
  town_code: string | null;
  lat: number;
  long: number;
};

export type getSellingContractsValidResponseData = {
  limit: number;
  offset: number;
  total_count: number;
  lat: number;
  long: number;
  selling_contracts: SellingContractOfMap[];
};

export type getSellingContactsMapResponse = getSellingContractsValidResponseData | undefined;

export const useIqfuProSellingContracts = () => {
  const [cookies] = useCookies(['expiry', 'access-token', 'client', 'uid', 'user-type']);

  const apiGet = async <T>(url: string): Promise<T> => {
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    });

    if (!response.ok) {
      const error = new Error(`code: ${response.status}, message: ${response.statusText}`);
      Sentry.captureException(error);
      console.error('通信に失敗しました', error);
      throw error;
    }

    return await response.json();
  };

  type getSellingContractsAPIResponse = {
    offset: number;
    limit: number;
    total_count: number;
    selling_contracts: SellingContract[];
    neighboring_selling_contracts: SellingContract[];
  };

  const getSellingContracts = async (
    userType: 'company' | 'financial_agency',
    queryParams: {
      property_type: string;
      town_code?: string;
      mansion_id?: number;
      limit?: number;
      offset?: number;
    },
  ): Promise<getSellingContractsAPIResponse> => {
    if (!queryParams.offset || queryParams.offset < 0) queryParams.offset = 0;
    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/${userType}/selling_contracts?${query}`;

    return await apiGet<getSellingContractsAPIResponse>(url);
  };

  const getSellingContractsDetail = async (params: {
    userType: 'company' | 'financial_agency';
    selling_contract_id: number;
  }): Promise<SellingContractType> => {
    const url = `${backendBasePath}/${params.userType}/selling_contracts/${params.selling_contract_id}`;

    return await apiGet<SellingContractType>(url);
  };

  type RequestViewInformationMapType = {
    success: boolean;
  };
  const requestViewInformationMap = async (params: {
    userType: 'company' | 'financial_agency';
    sellingContractId: number;
  }): Promise<RequestViewInformationMapType> => {
    const url = `${backendBasePath}/${params.userType}/selling_contracts/${params.sellingContractId}/information_map_request`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getSellingContractsMap = async (params: {
    userType: 'company' | 'financial_agency';
    queryParams: getSellingContractsMapQueryParams;
  }): Promise<getSellingContactsMapResponse> => {
    const query = queryString.stringify(params.queryParams);
    const url = `${backendBasePath}/${params.userType}/selling_contracts/map_index?${query}`;

    return await apiGet<getSellingContactsMapResponse>(url);
  };

  return { getSellingContracts, getSellingContractsMap, getSellingContractsDetail, requestViewInformationMap };
};

export default useIqfuProSellingContracts;
